import { Link } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";

import { showErrorMessage, showSuccessMessage } from "../common/message";
import {
  addEmailTemplate,
  deleteEmailTemplate,
  getEmailTemplateDetail,
  updateEmailTemplate,
} from "../../feature-module/crm/email-template/emailTemplateAPI";
import DefaultEditor from "react-simple-wysiwyg";

type EmailTemplateFormData = {
  templateName: string;
  subjectPart: string;
  htmlPart: string;
};

const defaultEmailTemplateFormData = {
  templateName: "",
  subjectPart: "",
  htmlPart: "",
};

type EmailTemplateModalProps = {
  action?: "edit" | "delete";
  recordId?: number;
  setRecordId: (recordId: any) => void;
  reload: boolean;
  setReload: (reload: any) => void; // Function that takes no arguments and returns void
};

type EmailTemplatePayloadType = {
  templateName: string;
  subjectPart: string;
  htmlPart: string;
  [key: string]: any; // Add this index signature
};

type emailTemplateFieldType = {
  templateName: string;
  subjectPart: string;
  htmlPart: string;
  [key: string]: any;
};

const addKeys = ["templateName", "subjectPart", "htmlPart"];

const editKeys = ["templateName", "subjectPart", "htmlPart"];

const emailTemplateField: emailTemplateFieldType = {
  templateName: "Name",
  subjectPart: "Subject",
  htmlPart: "HTML Content",
};

const EmailTemplateModal = ({
  action,
  recordId,
  setRecordId,
  reload,
  setReload,
}: EmailTemplateModalProps) => {
  const [formData, setFormData] = useState<EmailTemplateFormData>(
    defaultEmailTemplateFormData
  );
  const [isEmailTemplateDtlLoading, setIsEmailTemplateDtlLoading] =
    useState(false);

  const cancelAddRef = useRef<HTMLButtonElement>(null);
  const cancelEditRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchEmailTemplateDetail = async () => {
      try {
        setIsEmailTemplateDtlLoading(true);
        const { data } = await getEmailTemplateDetail(recordId);
        const dtlData = data?.data;

        setFormData({
          templateName: dtlData?.name,
          subjectPart: dtlData?.subjectPart,
          htmlPart: dtlData?.htmlPart,
        });

        console.log(dtlData);
      } catch (error) {
        console.error("Error fetching emailTemplate details:", error);
      }
      setIsEmailTemplateDtlLoading(false);
    };

    if (action === "edit" && recordId) {
      fetchEmailTemplateDetail();
    }
  }, [recordId, action]);

  const initFormData = () => {
    setFormData(defaultEmailTemplateFormData);
    setRecordId(null);
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newFormData = { ...formData, [e.target.name]: e.target.value };
    setFormData(newFormData);
  };

  const isValidPayload = (
    payload: EmailTemplatePayloadType,
    keys: string[]
  ) => {
    let error = "";
    keys.forEach((key) => {
      if ([undefined, "", null].includes(payload[key])) {
        if (!error) {
          error = `${emailTemplateField[key]} is required`;
        }
      }
    });

    if (error) {
      showErrorMessage(error);
      return false;
    }
    return true;
  };

  const handleCreateEmailTemplate = async () => {
    try {
      const payload: EmailTemplatePayloadType = {
        ...formData,
      };

      if (!isValidPayload(payload, addKeys)) {
        return;
      }

      await addEmailTemplate(payload);

      showSuccessMessage("Added emailTemplate successfully!");
      cancelAddRef.current?.click();
      setReload(!reload);
    } catch (error) {
      console.error("Error adding emailTemplates:", error);
    }
  };

  const handleEditEmailTemplate = async () => {
    try {
      const payload: EmailTemplatePayloadType = {
        ...formData,
      };

      if (!isValidPayload(payload, editKeys)) {
        return;
      }

      if (recordId) {
        await updateEmailTemplate(recordId, payload);

        showSuccessMessage("Edited emailTemplate successfully!");
        cancelEditRef.current?.click();
        setReload(!reload);
      }
    } catch (error) {
      console.error("Error updating emailTemplates:", error);
    }
  };

  const handleDeleteEmailTemplate = async () => {
    if (recordId) {
      try {
        await deleteEmailTemplate(recordId);
        showSuccessMessage("Deleted email template successfully!");
        setReload(!reload);
      } catch (error) {
        console.error("Error deleting email templates:", error);
      }
    }
  };

  const emailTemplateFormTsx = (
    <div className="row">
      <div className="col-md-12">
        <div className="mb-3">
          <label className="col-form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="templateName"
            onChange={handleInputChange}
            value={formData.templateName}
          />
        </div>
      </div>
      <div className="col-md-12">
        <div className="mb-3">
          <label className="col-form-label">
            Subject <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="subjectPart"
            onChange={handleInputChange}
            value={formData.subjectPart}
          />
        </div>
      </div>
      <div className="col-lg-12 col-md-12">
        <div className="mb-3">
          <label className="col-form-label">
            HTML Content<span className="text-danger"> *</span>
          </label>
          <DefaultEditor
            className="summernote"
            style={{ height: "400px" }}
            value={formData.htmlPart}
            onChange={(event) => {
              setFormData({ ...formData, htmlPart: event.target.value });
            }}
          />
        </div>
      </div>
    </div>
  );

  const addNewEmailTemplateTsx = (
    <div
      className="offcanvas offcanvas-end offcanvas-large"
      tabIndex={-1}
      id="offcanvas_add"
    >
      <div className="offcanvas-header border-bottom">
        <h4>Add Email Template</h4>
        <button
          type="button"
          className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={initFormData}
        >
          <i className="ti ti-x" />
        </button>
      </div>
      <div className="offcanvas-body">
        <form>
          <div>
            <div className="row">{emailTemplateFormTsx}</div>
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <button
              type="button"
              data-bs-dismiss="offcanvas"
              className="btn btn-light me-2"
              ref={cancelAddRef}
              onClick={initFormData}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreateEmailTemplate}
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const editEmailTemplateTsx = (
    <div
      className="offcanvas offcanvas-end offcanvas-large"
      tabIndex={-1}
      id="offcanvas_edit"
    >
      <div className="offcanvas-header border-bottom">
        <h4>Edit EmailTemplate</h4>
        <button
          type="button"
          className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={initFormData}
        >
          <i className="ti ti-x" />
        </button>
      </div>
      <div className="offcanvas-body">
        <form>
          <div className="pro-create">
            <div>
              <div className="row">{emailTemplateFormTsx}</div>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <button
              type="button"
              data-bs-dismiss="offcanvas"
              className="btn btn-light me-2"
              ref={cancelEditRef}
              onClick={initFormData}
            >
              Cancel
            </button>
            <button
              onClick={handleEditEmailTemplate}
              type="button"
              className="btn btn-primary"
              disabled={isEmailTemplateDtlLoading}
            >
              Edit
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const deleteEmailTemplateTsx = (
    <div className="modal fade" id="delete_emailTemplate" role="dialog">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body">
            <div className="text-center">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                <i className="ti ti-trash-x fs-36 text-danger" />
              </div>
              <h4 className="mb-2">Remove email template?</h4>
              <p className="mb-0">
                Are you sure you want to remove <br /> email template you
                selected.
              </p>
              <div className="d-flex align-items-center justify-content-center mt-4">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  onClick={handleDeleteEmailTemplate}
                  to="#"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                >
                  Yes, Delete it
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <>
        {addNewEmailTemplateTsx}
        {editEmailTemplateTsx}
      </>
      <>
        {deleteEmailTemplateTsx}
        {/* Add New View */}
        <div className="modal custom-modal fade" id="save_view" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New View</h5>
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="col-form-label">View Name</label>
                    <input type="text" className="form-control" />
                  </div>
                  <div className="modal-btn text-end">
                    <Link
                      to="#"
                      className="btn btn-light"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button type="submit" className="btn btn-danger">
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Add New View */}
      </>
    </>
  );
};

export default EmailTemplateModal;
